import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone } from "../controller/global";
import { submitLead } from "../controller/leads";
import { Images } from "../controller/Images";
import { FaBolt, FaDatabase, FaFileLines, FaRobot, FaShieldHalved, FaWandMagicSparkles } from "react-icons/fa6";

const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

export const generativeAISEO = {
  title: "Generative AI Development Services | InQmise",
  description:
    "Custom generative AI development: enterprise LLM applications, RAG systems and AI agents built on OpenAI, Claude and Gemini for real business workflows.",
  keywords:
    "Generative AI Development Services, Enterprise AI Development, LLM Development, AI Agent Development, LangChain, RAG, Vector Database, OpenAI, Claude",
  canonical: "https://inqmisetechnologies.com/generative-ai-development",
  og: {
    title: "Generative AI Development Services | InQmise",
    description:
      "Custom generative AI development: enterprise LLM applications, RAG systems and AI agents built on OpenAI, Claude and Gemini for real business workflows.",
    url: "https://inqmisetechnologies.com/generative-ai-development",
    type: "website",
    image: "https://inqmisetechnologies.com/logo.png",
  },
  twitter: {
    card: "summary_large_image",
    title: "Generative AI Development Services | InQmise",
    description:
      "Supercharge your business with custom Generative AI development services. We build enterprise LLMs, RAG systems & AI agents using OpenAI, Claude & Gemini.",
    image: "https://inqmisetechnologies.com/logo.png",
  },
};

const SYSTEMS = [
  {
    num: "01 / RETRIEVE",
    title: "Your knowledge, finally in context.",
    copy: "We map sources, permissions, freshness and intent before a model ever sees a question.",
    tags: ["Document intelligence", "Permission-aware search", "Source citations"],
  },
  {
    num: "02 / REASON",
    title: "Decisions with a visible trail.",
    copy: "We compose retrieval, tools and human review into workflows that are explainable by design.",
    tags: ["Agent workflows", "Evaluation harnesses", "Human approval"],
  },
  {
    num: "03 / IMPROVE",
    title: "Every use makes the system sharper.",
    copy: "Real-world outcomes feed an operating loop, so the product gets better without becoming unpredictable.",
    tags: ["Quality monitoring", "Feedback loops", "Cost controls"],
  },
];

const PROCESS_STEPS = [
  { n: "01", t: "Find the leverage", d: "Spot the decisions and repetitive work where good context creates real value." },
  { n: "02", t: "Build the proof", d: "Ship a focused, production-shaped pilot around one measurable outcome." },
  { n: "03", t: "Make it dependable", d: "Stress-test quality, safety, permissions and operational ownership." },
  { n: "04", t: "Scale with intent", d: "Extend what works, using a clear roadmap and an evidence trail." },
];

const SERVICES = [
  { Icon: FaDatabase, title: "RAG applications & knowledge systems", copy: "Search, synthesis and grounded answers that give teams a trustworthy way to work with knowledge they already own.", featured: true },
  { Icon: FaWandMagicSparkles, title: "AI opportunity & strategy", copy: "Choose the use cases worth pursuing and define how success will be measured." },
  { Icon: FaRobot, title: "Agent workflows", copy: "Coordinate reasoning, tools and review for work that needs more than a chat window." },
  { Icon: FaShieldHalved, title: "Evaluation & governance", copy: "Put quality, traceability, safety and cost into the product, not a side document." },
  { Icon: FaFileLines, title: "Integration & enablement", copy: "Connect the right systems and leave your team able to operate what we build." },
  { Icon: FaBolt, title: "Product acceleration", copy: "Embed generative AI directly into your product, from a working prototype to a release your team can maintain." },
];

const EVAL_CRITERIA = [
  { a: "Accuracy on your tasks", b: "EVALUATE" },
  { a: "Latency at peak demand", b: "SIMULATE" },
  { a: "Cost at real usage", b: "MODEL" },
  { a: "Governance and data boundaries", b: "VERIFY" },
  { a: "Fit for the task at hand", b: "MATCH" },
];

const EVAL_METRICS = [
  { a: "99.2%", b: "answers grounded in approved sources" },
  { a: "−42%", b: "time spent locating a usable answer" },
  { a: "3.1×", b: "faster first-draft workflow completion" },
  { a: "24/7", b: "quality signals, not just uptime signals" },
];

const CASE_RESULTS = [
  { a: "65%", b: "faster first response" },
  { a: "31%", b: "less repeat work" },
  { a: "8 wks", b: "from discovery to pilot" },
  { a: "100%", b: "answers source-linked" },
];

const INDUSTRIES = [
  { a: "Financial services", b: "Controlled knowledge for advisor and operations workflows." },
  { a: "Healthcare", b: "Find and summarize approved information without losing the trail." },
  { a: "B2B software", b: "Give support and success teams a reliable second brain." },
  { a: "Professional services", b: "Make collective expertise easier to put to work." },
  { a: "Manufacturing", b: "Bring manuals, incident history and field knowledge together." },
  { a: "Legal & compliance", b: "Speed up research while making sources and approvals explicit." },
  { a: "Retail & commerce", b: "Help teams decide quickly with the right operational context." },
  { a: "Internal platforms", b: "Turn a growing knowledge base into a service people use." },
];

// What moves the price (SEO plan: competitors all answer cost; we explain the
// drivers and quote after scoping rather than publish invented numbers).
const COST_FACTORS = [
  { t: "Internal or customer-facing", d: "An internal assistant used by your team needs less polish and fewer safeguards than a feature your customers use directly." },
  { t: "The data behind it", d: "Clean, well-organized documents are quick to use. Scattered files, scanned PDFs and data locked in old systems add preparation work." },
  { t: "How good is good enough", d: "Higher accuracy targets need a larger evaluation set, more testing and often human review steps, which add build time." },
  { t: "Model and hosting choice", d: "Hosted APIs are fastest to start. Self-hosted open models cost more to set up but can be cheaper at high volume or required for data control." },
];

const FAQS = [
  {
    q: "What does a first engagement look like?",
    a: "We begin with a focused discovery to identify the decision, knowledge, and success measure that matter most, typically followed by a working pilot within 4-6 weeks.",
  },
  {
    q: "What is RAG, and why does it matter for accuracy?",
    a: "Retrieval-Augmented Generation connects a language model to your own documents through a vector database, so answers are grounded in verified content rather than the model's general training data — the single biggest lever for reducing incorrect answers.",
  },
  {
    q: "How is an AI agent different from a chatbot?",
    a: "A chatbot answers questions. An agent plans and executes a sequence of steps against defined tools and systems, within explicit limits on what it's permitted to do, and reports back what it did.",
  },
  {
    q: "Can you work with our existing cloud and data stack?",
    a: "Yes. We design around the systems, permissions, and operating practices you already have. OpenAI, Anthropic Claude, Google Gemini, and open-weight models are all considered, chosen on evidence rather than preference.",
  },
  {
    q: "How do you test whether an AI answer is good enough?",
    a: "We define evaluation sets, groundedness checks, and human review points before launch, and score every candidate model on accuracy, latency, cost, and governance fit.",
  },
  {
    q: "Will our team be able to own the system once it's live?",
    a: "Yes. Enablement, documentation, and a clear operating owner are built into every engagement, not left as an afterthought.",
  },
  {
    q: "What are generative AI development services?",
    a: "Building applications that use generative AI models to write, summarize, answer questions or extract information from your own data, such as internal assistants, document processing and customer support tools, with the testing and controls needed to run them in production.",
  },
  {
    q: "How much does generative AI development cost?",
    a: "It depends on the use case, the data involved and the accuracy required. A focused internal assistant over your documents is a much smaller project than a customer-facing product. We scope a first use case and quote a fixed price for it.",
  },
  {
    q: "How long does it take to build a generative AI application?",
    a: "A first focused use case typically reaches a working pilot in weeks, not months. Wider rollouts follow once the pilot proves its value on real users.",
  },
  {
    q: "Is our company data safe with generative AI?",
    a: "Yes, when it is built properly: your data stays in your cloud, access follows your existing permissions, and we use model providers and settings that do not train on your data.",
  },
  {
    q: "Who owns the code and the AI system you build?",
    a: "You do. Source code, prompts, evaluation sets, trained model weights and documentation are handed over and belong to you, and we sign an NDA before any detailed discussion.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the engine panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

const GenerativeAIDevelopment = () => {
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = generativeAISEO.title;

    const description = document.createElement("meta");
    description.name = "description";
    description.content = generativeAISEO.description;
    document.head.appendChild(description);

    const keywords = document.createElement("meta");
    keywords.name = "keywords";
    keywords.content = generativeAISEO.keywords;
    document.head.appendChild(keywords);

    const ogTitle = document.createElement("meta");
    ogTitle.setAttribute("property", "og:title");
    ogTitle.content = generativeAISEO.og.title;
    document.head.appendChild(ogTitle);

    const ogDesc = document.createElement("meta");
    ogDesc.setAttribute("property", "og:description");
    ogDesc.content = generativeAISEO.og.description;
    document.head.appendChild(ogDesc);

    const ogUrl = document.createElement("meta");
    ogUrl.setAttribute("property", "og:url");
    ogUrl.content = generativeAISEO.og.url;
    document.head.appendChild(ogUrl);

    const ogType = document.createElement("meta");
    ogType.setAttribute("property", "og:type");
    ogType.content = generativeAISEO.og.type;
    document.head.appendChild(ogType);

    const ogImage = document.createElement("meta");
    ogImage.setAttribute("property", "og:image");
    ogImage.content = generativeAISEO.og.image;
    document.head.appendChild(ogImage);

    const twitterCard = document.createElement("meta");
    twitterCard.name = "twitter:card";
    twitterCard.content = generativeAISEO.twitter.card;
    document.head.appendChild(twitterCard);

    const twitterTitle = document.createElement("meta");
    twitterTitle.name = "twitter:title";
    twitterTitle.content = generativeAISEO.twitter.title;
    document.head.appendChild(twitterTitle);

    const twitterDesc = document.createElement("meta");
    twitterDesc.name = "twitter:description";
    twitterDesc.content = generativeAISEO.twitter.description;
    document.head.appendChild(twitterDesc);

    const twitterImage = document.createElement("meta");
    twitterImage.name = "twitter:image";
    twitterImage.content = generativeAISEO.twitter.image;
    document.head.appendChild(twitterImage);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", generativeAISEO.canonical);

    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Generative AI Development Services",
      "description": generativeAISEO.description,
      "url": generativeAISEO.canonical,
      "provider": { "@id": "https://inqmisetechnologies.com/#organization" },
      "areaServed": ["IN", "US", "GB", "CA", "AU", "AE", "SG"],
      "availableLanguage": ["en-IN"],
      "serviceType": "Generative AI Development",
    });
    document.head.appendChild(schemaScript);

    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://inqmisetechnologies.com" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://inqmisetechnologies.com/services" },
        { "@type": "ListItem", "position": 3, "name": "Generative AI Development", "item": generativeAISEO.canonical },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname":
        return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject":
        return value.trim() ? "" : "Subject is required.";
      case "description":
        return value.trim() ? "" : "Tell us a little about your project.";
      default:
        return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }

    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => {
        setContactStatus("error");
      });
  };

  const openContactModal = () => setIsOpen(true);

  return (
    <main className="editorial-ai pt-20">
      {/* HERO */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">Generative AI systems · built to perform</p>
          <h1>
            Generative AI development services that <em>earn their place</em> in your business.
          </h1>
          <p className="ea-lede">
            Our generative AI development services take you from retrieval to rollout, turning fragmented knowledge into reliable AI products your teams can use, measure, and trust.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to plan your AI system">
              Plan your AI system ↗
            </button>
            <a className="ea-text-link" href="#systems">See the approach</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / KNOWLEDGE ENGINE <b>LIVE</b></div>
          <h2>What should our support team know before the next customer call?</h2>
          <div className="ea-query"><small>RETRIEVED FROM 18 SOURCES</small>Enterprise support playbook · Q2 release notes · customer health snapshot</div>
          <div className="ea-query"><small>GROUNDED RESPONSE</small>Three priority issues found. Suggested action plan is ready for review.</div>
          <div className="ea-flow"><b>Sources</b><i>→</i><b>Context</b><i>→</i><b>Answer</b></div>
        </Reveal>
      </header>

      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>TRUSTED BY GROWING BUSINESSES</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* HOW THE SYSTEM WORKS */}
      <section className="ea-wrap ea-section" id="systems">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">The practical layer</div>
            <h2>RAG, made operational.</h2>
          </div>
          <p>No hand-wavy chatbot experiments. Every component exists to make an answer more useful, safer, or easier to improve.</p>
        </Reveal>

        {SYSTEMS.map((item, i) => (
          <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={item.title}>
            <div className="ea-num">{item.num}</div>
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
            <div className="ea-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          </Reveal>
        ))}
      </section>

      {/* PROCESS */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">A disciplined route to value</div>
            <h2>Make the right thing<br />before scaling it.</h2>
          </div>
          <p>A four-part engagement designed to replace uncertainty with proof, then turn proof into an operating system.</p>
        </Reveal>
        <div className="ea-process">
          {PROCESS_STEPS.map((step, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-step" key={step.n}>
              <span>{step.n}</span>
              <i>↗</i>
              <h3>{step.t}</h3>
              <p>{step.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">What we build</div>
              <h2>Modular services.<br />One accountable system.</h2>
            </div>
            <p>Bring us in for a focused need or keep us close from the first prototype to the millionth query.</p>
          </Reveal>
          <div className="ea-cards">
            {SERVICES.map((item, i) => (
              <Reveal as="article" i={(i % 4) + 1} reduceMotion={reduceMotion} key={item.title} className={`ea-card ${item.featured ? "ea-featured" : ""}`}>
                <div className="ea-icon"><item.Icon aria-hidden="true" /></div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MODEL EVALUATION */}
      <section className="ea-models">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Model independence</div>
              <h2>Pick models for the job,<br />not for the pitch.</h2>
            </div>
            <p>We keep an objective view of the stack: quality, latency, cost and control are all part of the conversation.</p>
          </Reveal>
          <div className="ea-model-layout">
            <Reveal as="div" className="ea-model-panel">
              <h3>How we make choices</h3>
              {EVAL_CRITERIA.map((row) => (
                <div key={row.a}><span>{row.a}</span><small>{row.b}</small></div>
              ))}
            </Reveal>
            <div className="ea-metrics">
              {EVAL_METRICS.map((m, i) => (
                <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-metric" key={m.a}>
                  <strong>{m.a}</strong>
                  <span>{m.b}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDY */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Selected outcome</div>
            <h2>Less search.<br />More sure-footed work.</h2>
          </div>
          <p>A knowledge assistant for a multi-market operations team, designed around questions that delay customer delivery.</p>
        </Reveal>
        <Reveal as="div" className="ea-case">
          <div>
            <div className="ea-eyebrow">OPERATIONS / B2B SAAS</div>
            <h3>One source of truth for work that cannot afford a guess.</h3>
            <p>The system combines internal playbooks, recent incidents and customer context with citations and clear hand-offs when a person should take over.</p>
            <button className="ea-text-link" onClick={openContactModal}>Discuss a similar project</button>
          </div>
          <div className="ea-results">
            {CASE_RESULTS.map((r) => (
              <div key={r.a}><b>{r.a}</b><span>{r.b}</span></div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* INDUSTRIES */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Where it works</div>
            <h2>Built around the<br />work, not the trend.</h2>
          </div>
          <p>We design for environments where context, accountability and speed all matter at the same time.</p>
        </Reveal>
        <div className="ea-industries">
          {INDUSTRIES.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ea-industry" key={item.a}>
              <b>{item.a}</b>
              <p>{item.b}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COST — what drives the price */}
      <section className="ea-wrap ea-section" id="cost">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Cost</div>
            <h2>What drives the cost of<br />generative AI development.</h2>
          </div>
          <p>Generative AI projects vary widely in cost. These are the factors that move the number, and we pin them down before giving a fixed price for the first use case.</p>
        </Reveal>
        <div className="aa-pill-grid">
          {COST_FACTORS.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ai-industry-card" key={item.t} style={{ flex: "1 1 260px" }}>
              <h3>{item.t}</h3>
              <p>{item.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>Generative AI development FAQs</h2>
            <p>Short answers to the questions we are usually asked before an engagement begins.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `faq-panel-${i}`;
            const buttonId = `faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  >
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="ea-cta" aria-labelledby="contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">A practical first step</div>
            <h2 id="contact-heading">Bring us the question that has real stakes.</h2>
            <p>We'll help you frame the opportunity, identify the constraints and decide what a useful first proof should look like.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span>
            <h3>Build with more certainty.</h3>
            <p>Tell us what your team needs to know, decide or deliver faster.</p>

            <form onSubmit={handleContactSubmit} aria-label="Generative AI development project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="gen-ai-fullname">Full Name</label>
                <input
                  id="gen-ai-fullname"
                  name="fullname"
                  type="text"
                  value={formData.fullname}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)}
                  aria-describedby={contactErrors.fullname ? "gen-ai-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="gen-ai-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="gen-ai-phone">Phone Number (optional)</label>
                  <input
                    id="gen-ai-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)}
                    aria-describedby={contactErrors.phone ? "gen-ai-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="gen-ai-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="gen-ai-email">Email</label>
                  <input
                    id="gen-ai-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)}
                    aria-describedby={contactErrors.email ? "gen-ai-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="gen-ai-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="gen-ai-subject">Subject</label>
                <input
                  id="gen-ai-subject"
                  name="subject"
                  type="text"
                  placeholder="e.g., A knowledge assistant for our support team"
                  value={formData.subject}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)}
                  aria-describedby={contactErrors.subject ? "gen-ai-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="gen-ai-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="gen-ai-description">Project Details</label>
                <textarea
                  id="gen-ai-description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)}
                  aria-describedby={contactErrors.description ? "gen-ai-description-error" : undefined}
                />
                {contactErrors.description && <p id="gen-ai-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Submitting…" : "Start a conversation ↗"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default GenerativeAIDevelopment;
